import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAirportAutocomplete, useStarredAirports } from '../../../hooks';
import type { AirportAutocomplete } from '../../../services/redux/api/airport.api';
import { useLazyAutocompleteAirportsQuery } from '../../../services/redux/api/airport.api';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

interface FormAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSelect?: (airport: AirportAutocomplete) => void;
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  onBlur?: (value: string) => void;
}

/** Format the enriched display string shown in the input after selection */
function formatDisplay(airport: AirportAutocomplete): string {
  if (airport.cityName) {
    return `${airport.iataAirportCode} — ${airport.cityName}, ${airport.countryCode}`;
  }
  return airport.iataAirportCode;
}

/**
 * Autocomplete input for airport IATA codes
 * Provides real-time search with dropdown suggestions and starred favorites
 */
export function FormAutocomplete({
  label,
  value,
  onChange,
  onSelect,
  placeholder,
  required,
  error,
  onBlur,
}: FormAutocompleteProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [codeValue, setCodeValue] = useState(value);
  const [displayValue, setDisplayValue] = useState(value);
  const [isShowingDetail, setIsShowingDetail] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { airports: hookAirports, loading, search } = useAirportAutocomplete({ delay: 300, limit: 10 });
  const allAirports = Array.isArray(hookAirports) ? hookAirports : [];
  const airports = allAirports.filter(a =>
    a.iataAirportCode.startsWith(codeValue.toUpperCase())
  );

  const { starredAirports, isStarred: isAirportStarred, toggle: toggleAirportStar } = useStarredAirports();

  // Persistent cache of airport metadata accumulated across searches + favorite lookups
  const seenAirportsRef = useRef<Map<string, AirportAutocomplete>>(new Map());
  allAirports.forEach(a => seenAirportsRef.current.set(a.iataAirportCode, a));

  // Lazy query to fetch metadata for starred airports not yet in cache
  const [fetchAirport] = useLazyAutocompleteAirportsQuery();
  const pendingLookupsRef = useRef<Set<string>>(new Set());
  const [favVersion, setFavVersion] = useState(0);

  // Proactively fetch metadata for any starred code we haven't seen yet
  useEffect(() => {
    starredAirports.forEach(code => {
      if (!seenAirportsRef.current.has(code) && !pendingLookupsRef.current.has(code)) {
        pendingLookupsRef.current.add(code);
        fetchAirport({ query: code, limit: 5 })
          .unwrap()
          .then(results => {
            results.forEach(a => seenAirportsRef.current.set(a.iataAirportCode, a));
            pendingLookupsRef.current.delete(code);
            setFavVersion(v => v + 1);
          })
          .catch(() => {
            pendingLookupsRef.current.delete(code);
          });
      }
    });
  }, [starredAirports, fetchAirport]);

  // Resolve starred codes to full objects using the accumulated cache
  const favoriteItems = useMemo(() => {
    return Array.from(starredAirports).map(code => {
      return seenAirportsRef.current.get(code)
        || { iataAirportCode: code, utcVariant: '', cityName: '', airportName: '', countryCode: '' } as AirportAutocomplete;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starredAirports, allAirports, favVersion]);

  // Which mode the dropdown is in
  const isSearchMode = codeValue.length >= 1;
  const activeItems = isSearchMode ? airports : favoriteItems;

  const hasError = error && error.trim() !== '';

  // Sync from parent value prop
  useEffect(() => {
    setCodeValue(value);
    if (!isShowingDetail) {
      setDisplayValue(value);
    }
    if (value !== codeValue) {
      setDisplayValue(value);
      setIsShowingDetail(false);
    }
  }, [value]);

  // Auto-resolve enriched display on mount / when data loads (only when not focused)
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    if (codeValue && !isShowingDetail && allAirports.length > 0) {
      const match = allAirports.find(a => a.iataAirportCode === codeValue);
      if (match) {
        setDisplayValue(formatDisplay(match));
        setIsShowingDetail(true);
      }
    }
  }, [allAirports, codeValue]);

  // Also resolve from cache when favVersion updates (covers starred lookups)
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    if (codeValue && !isShowingDetail) {
      const cached = seenAirportsRef.current.get(codeValue);
      if (cached) {
        setDisplayValue(formatDisplay(cached));
        setIsShowingDetail(true);
      }
    }
  }, [favVersion]);

  // Update dropdown position when it opens
  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const updatePosition = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();

      const handleScroll = (e: Event) => {
        if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
          return;
        }
        setShowDropdown(false);
      };

      const handleResize = () => {
        setShowDropdown(false);
      };

      const handleClickOutside = (e: MouseEvent) => {
        if (
          inputRef.current && !inputRef.current.contains(e.target as Node) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
        ) {
          setShowDropdown(false);
        }
      };

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    setCodeValue(raw);
    setDisplayValue(raw);
    setIsShowingDetail(false);
    onChange(raw);

    if (raw.length >= 1) {
      search(raw);
      setShowDropdown(true);
      setSelectedIndex(-1);
    } else {
      setShowDropdown(starredAirports.size > 0);
      setSelectedIndex(-1);
    }
  };

  const handleSelectAirport = (airport: AirportAutocomplete) => {
    setCodeValue(airport.iataAirportCode);
    setDisplayValue(formatDisplay(airport));
    setIsShowingDetail(true);
    onChange(airport.iataAirportCode);
    onSelect?.(airport);
    setShowDropdown(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
      setSelectedIndex(-1);
      onBlur?.(codeValue);
    }, 200);
  };

  const handleFocus = () => {
    if (isShowingDetail) {
      setDisplayValue(codeValue);
      setIsShowingDetail(false);
      if (codeValue.length >= 1) {
        search(codeValue);
        setShowDropdown(true);
      } else if (starredAirports.size > 0) {
        setShowDropdown(true);
      }
    } else if (codeValue.length >= 1 && airports.length > 0) {
      setShowDropdown(true);
    } else if (starredAirports.size > 0) {
      setShowDropdown(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || activeItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, activeItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < activeItems.length) {
          handleSelectAirport(activeItems[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const renderDropdown = () => {
    if (!showDropdown) return null;
    if (!isSearchMode && favoriteItems.length === 0) return null;

    const dropdown = (
      <div
        ref={dropdownRef}
        onMouseDown={(e) => e.preventDefault()}
        style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          maxHeight: '240px',
          overflowY: 'auto',
          overflowX: 'hidden',
          zIndex: 9999,
        }}
        className="bg-white border border-gray-200 rounded-lg shadow-xl"
      >
        {/* Favorites mode — empty input */}
        {!isSearchMode && favoriteItems.length > 0 && (
          <>
            <div className="px-3 pt-2 pb-1 flex items-center gap-1.5">
              <StarIcon sx={{ fontSize: 12 }} className="text-amber-400" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Favorites</span>
            </div>
            <ul className="py-0.5 pb-1">
              {favoriteItems.map((airport, index) => (
                <li key={airport.iataAirportCode}>
                  <button
                    type="button"
                    onClick={() => handleSelectAirport(airport)}
                    className={`w-full px-3 py-2 text-left transition-colors
                      ${index === selectedIndex
                        ? 'bg-primary-50'
                        : 'hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-1">
                          <span className={`font-bold text-sm font-mono ${index === selectedIndex ? 'text-primary-700' : 'text-gray-900'}`}>
                            {airport.iataAirportCode}
                          </span>
                          {airport.countryCode && (
                            <span className={`text-sm font-mono ${index === selectedIndex ? 'text-primary-500' : 'text-gray-500'}`}>
                              - {airport.countryCode}
                            </span>
                          )}
                        </div>
                        {airport.cityName && (
                          <div className="flex justify-between items-center mt-0.5">
                            <span className="text-xs text-gray-400 uppercase tracking-wide">
                              {airport.cityName}
                            </span>
                            <span className="text-xs font-mono text-gray-400 ml-2">
                              {airport.utcVariant}
                            </span>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAirportStar(airport.iataAirportCode);
                        }}
                        className="p-0 border-0 bg-transparent cursor-pointer flex items-center ml-2"
                      >
                        <StarIcon sx={{ fontSize: 14 }} className="text-amber-400" />
                      </button>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Search mode — typing */}
        {isSearchMode && (
          <>
            {loading && (
              <div className="px-3 py-2.5 text-xs text-gray-400">
                Searching airports...
              </div>
            )}

            {!loading && airports.length === 0 && (
              <div className="px-3 py-2.5 text-xs text-gray-400">
                No airports found
              </div>
            )}

            {!loading && airports.length > 0 && (
              <ul className="py-1">
                {airports.map((airport, index) => (
                  <li key={airport.iataAirportCode}>
                    <button
                      type="button"
                      onClick={() => handleSelectAirport(airport)}
                      className={`w-full px-3 py-2 text-left transition-colors
                        ${index === selectedIndex
                          ? 'bg-primary-50'
                          : 'hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-1">
                            <span className={`font-bold text-sm font-mono ${index === selectedIndex ? 'text-primary-700' : 'text-gray-900'}`}>
                              {airport.iataAirportCode}
                            </span>
                            {airport.countryCode && (
                              <span className={`text-sm font-mono ${index === selectedIndex ? 'text-primary-500' : 'text-gray-500'}`}>
                                - {airport.countryCode}
                              </span>
                            )}
                          </div>
                          <div className="flex justify-between items-center mt-0.5">
                            <span className="text-xs text-gray-400 uppercase tracking-wide">
                              {airport.cityName}
                            </span>
                            <span className="text-xs font-mono text-gray-400 ml-2">
                              {airport.utcVariant}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAirportStar(airport.iataAirportCode);
                          }}
                          className="p-0 border-0 bg-transparent cursor-pointer flex items-center ml-2"
                        >
                          {isAirportStarred(airport.iataAirportCode) ? (
                            <StarIcon sx={{ fontSize: 14 }} className="text-amber-400" />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 14 }} className="text-gray-400 hover:text-amber-300" />
                          )}
                        </button>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    );

    return createPortal(dropdown, document.body);
  };

  return (
    <label className="block mb-2">
      <span className="block mb-1.5 text-base font-semibold text-gray-600">
        {label}
        {required && <span className="text-danger-500 ml-1">*</span>}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoCapitalize="characters"
        className={`w-full px-3 py-2.5 bg-white border rounded-lg text-gray-900 text-lg outline-none transition-all
          ${isShowingDetail ? 'text-base' : ''}
          ${hasError
            ? 'border-danger-500 focus:border-danger-500 focus:ring-3 focus:ring-danger-500/10'
            : 'border-gray-300 focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10'
          }`}
      />

      {renderDropdown()}

    </label>
  );
}
